import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Typography
  } from "@mui/material";
import { AlertColor } from "@mui/material/Alert";

interface CustomSnackBarProps {
  open: boolean;
  onClose: () => void;
  type: AlertColor;
  title?: string;
  message: string;
  autoHideDuration?: number;
}

/**
 * Komponent niestandardowego SnackBar
 * Zgodny z zasadą pojedynczej odpowiedzialności - tylko wyświetlanie powiadomień
 */
export default function CustomSnackBar({
  open,
  onClose,
  type,
  title,
  message,
  autoHideDuration = 6000,
}: CustomSnackBarProps) {
  if (!open) {
    return null;
  }

  // Pastelowe kolory dla różnych typów alertów
  const getPastelColors = (severity: AlertColor) => {
    switch (severity) {
      case "success":
        return {
          backgroundColor: "#e8f5e8", // Bardzo jasny zielony
          color: "#2e7d32", // Ciemniejszy zielony dla tekstu
          borderColor: "#c8e6c9", // Jasny zielony dla obramowania
        };
      case "error":
        return {
          backgroundColor: "#ffebee", // Bardzo jasny różowy
          color: "#c62828", // Ciemniejszy czerwony dla tekstu
          borderColor: "#ffcdd2", // Jasny różowy dla obramowania
        };
      case "warning":
        return {
          backgroundColor: "#fff8e1", // Bardzo jasny żółty
          color: "#f57c00", // Ciemniejszy pomarańczowy dla tekstu
          borderColor: "#ffecb3", // Jasny żółty dla obramowania
        };
      case "info":
        return {
          backgroundColor: "#e3f2fd", // Bardzo jasny niebieski
          color: "#1565c0", // Ciemniejszy niebieski dla tekstu
          borderColor: "#bbdefb", // Jasny niebieski dla obramowania
        };
      default:
        return {
          backgroundColor: "#f5f5f5", // Bardzo jasny szary
          color: "#424242", // Ciemniejszy szary dla tekstu
          borderColor: "#e0e0e0", // Jasny szary dla obramowania
        };
    }
  };

  const colors = getPastelColors(type);

  // Auto-hide po określonym czasie
  React.useEffect(() => {
    if (open && autoHideDuration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [open, autoHideDuration, onClose]);

  return (
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        right: "20px",
        zIndex: 1000,
        maxWidth: "400px",
        minWidth: "300px",
      }}
    >
      <Alert
        onClose={onClose}
        severity={type}
        variant="outlined"
        sx={{
          width: "100%",
          backgroundColor: colors.backgroundColor,
          color: colors.color,
          borderColor: colors.borderColor,
          "& .MuiAlert-icon": {
            color: colors.color,
          },
          "& .MuiAlert-action": {
            color: colors.color,
          },
        }}
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        <Typography variant="body2">{message}</Typography>
      </Alert>
    </div>
  );
}

